import { MutableRefObject, ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import FormStepper from '@/components/ui/FormStepper/FormStepper';
import SuccessScreen from '@/components/ui/SuccessScreen/SuccessScreen';
import ConfirmationDialog from '@/components/ui/ConfirmationDialog/ConfirmationDialog';
import { StepDefinition } from '@/components/ui/FormStepper/types';
import { SearchableSelect } from '@/pages/newSupported/components/LabeledInput';
import { useGetAuthApi, usePostAuthApi } from '@/hooks/UseApiCalls';
import useBanks from '@/hooks/useBanks';
import useFundContext from '@/contexts/Fund/useFundContext';
import { bankCollectionLocksUrl } from '../bankStandingOrderActions';
import { describeActionError } from '../standingOrderActions';
import {
  BANK_BILLING_DAYS,
  BANK_BY_EMAIL,
  BRANCH_FAX_LOOKUP_URL,
  BankOrderErrors,
  BankOrderField,
  BankOrderForm,
  CreateBankStandingOrderRequest,
  CreateBankStandingOrderResult,
  MANDATE_FORM_ACCEPT,
  MANDATE_SETUP_DAYS,
  MandateAnswer,
  buildCreateRequest,
  createBankOrderUrl,
  emptyBankOrderForm,
  firstCollectionDate,
  formDeliveryOf,
  hasInput,
  lastCollectionDate,
  parseAmount,
  parsePayments,
  readIsoDate,
  validateDetails,
  validateMandateAnswer,
  validateMandateForm,
} from './bankOrderCreation';
import styles from '../NewStandingOrder/NewStandingOrder.module.css';
import ownStyles from './NewBankStandingOrder.module.css';

interface NewBankStandingOrderProps {
  fundId: number;
  /** Closes the dialog this form sits in. The form has no dialog of its own. */
  onClose: () => void;
  /** The form's own answer to "may the dialog close now?", read by the dialog around it. */
  closeRequestRef: MutableRefObject<(() => void) | null>;
  onRefreshList?: () => void;
}

const PREFIX = 'standingOrders.newBankOrder';
/* The credit form's words wherever the two forms ask the same thing. */
const CREDIT = 'standingOrders.newOrder';

/** The file without the data-URL prefix, as the server stores documents. */
const readBase64 = (file: File): Promise<string> => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => {
    const text = String(reader.result ?? '');
    resolve(text.slice(text.indexOf(',') + 1));
  };
  reader.onerror = () => reject(reader.error);
  reader.readAsDataURL(file);
});

const newRequestId = (): string => crypto.randomUUID();

/**
 * A new bank standing order (HAT-1478): does the donor's bank already hold a mandate; the details, the
 * same either way; if there is no mandate, the signed form and how it reaches the bank; then the order
 * restated, with its first collection, before it is set up. The office's spec put the form before the
 * details; it comes after them here because how it goes depends on the bank - by e-mail to Hapoalim,
 * by fax to any other - and the bank is one of the details. The coordinator reads of the order and the
 * bank - where the order is kept is the office's business.
 */
const NewBankStandingOrder = ({
  fundId, onClose, closeRequestRef, onRefreshList,
}: NewBankStandingOrderProps) => {
  const { t, i18n } = useTranslation();
  const { currentFund } = useFundContext();
  const [form, setForm] = useState<BankOrderForm>(emptyBankOrderForm);
  const [mandateFile, setMandateFile] = useState<File | null>(null);
  const [errors, setErrors] = useState<BankOrderErrors>({});
  const [status, setStatus] = useState<'ready' | 'submitting' | 'success' | 'error'>('ready');
  const [errorMessage, setErrorMessage] = useState('');
  const [created, setCreated] = useState<CreateBankStandingOrderResult | null>(null);
  const [showDiscardWarning, setShowDiscardWarning] = useState(false);
  /* One per form: a second press, or a retry after a lost answer, must not make a second order. */
  const [requestId, setRequestId] = useState(newRequestId);
  const fileInputRef = useRef<HTMLInputElement>(null);

  /*
   * The dates the office has locked for its bank file: a first collection on one would not be in
   * the file, so it moves to the next month. Behind the actions flag on the server; without it there
   * is nothing to say, and the server works the date out again anyway.
   */
  const { data: lockedDates } = useGetAuthApi<string[]>(bankCollectionLocksUrl(fundId), undefined, { retry: false });

  const createMutation = usePostAuthApi<CreateBankStandingOrderResult, CreateBankStandingOrderRequest>(
    createBankOrderUrl(fundId),
    {
      onSuccess: (result) => {
        setCreated(result);
        setStatus('success');
      },
      onError: (error) => {
        setStatus('error');
        setErrorMessage(describeCreateError(error));
      },
    },
  );

  const describeCreateError = (error: unknown): string => {
    const data = (error as { response?: { data?: { code?: string } } })?.response?.data;
    const code = typeof data === 'object' && data ? data.code : undefined;
    if (code) {
      const key = `${PREFIX}.rejections.${code}`;
      const text = t(key);
      if (text !== key) return text;
    }
    return describeActionError(error, t, true);
  };

  const update = (patch: Partial<BankOrderForm>) => {
    setForm((current) => ({ ...current, ...patch }));
    // A message goes as soon as its field is touched, not at the next Continue.
    setErrors((current) => {
      const touched = Object.keys(patch) as BankOrderField[];
      if (!touched.some((field) => current[field])) return current;
      const next = { ...current };
      touched.forEach((field) => { delete next[field]; });
      return next;
    });
  };

  const onInput = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    update({ [e.target.name]: e.target.value } as Partial<BankOrderForm>);

  const onDigits = (e: React.ChangeEvent<HTMLInputElement>) =>
    update({ [e.target.name]: e.target.value.replace(/\D/g, '') } as Partial<BankOrderForm>);

  const dateText = (date: Date) =>
    date.toLocaleDateString(i18n.language, { day: 'numeric', month: 'long', year: 'numeric' });
  const money = (amount: number) =>
    amount.toLocaleString(i18n.language, { style: 'currency', currency: 'ILS', maximumFractionDigits: 2 });

  const fieldError = (name: BankOrderField) => (errors[name] ? (
    <span id={`bank-order-${name}-error`} role="alert" className={styles.fieldError}>
      {t(`${PREFIX}.errors.${errors[name]}`)}
    </span>
  ) : null);

  const fieldClass = (name: BankOrderField) => `${styles.field} ${errors[name] ? styles.fieldInvalid : ''}`;

  const describedBy = (name: BankOrderField) => (errors[name] ? `bank-order-${name}-error` : undefined);

  const label = (key: string, required = false): ReactNode => (
    <label>
      {t(key)}
      {required && <span className={styles.required}>*</span>}
    </label>
  );

  const textField = (name: keyof BankOrderForm, key: string, options: {
    required?: boolean; maxLength?: number; type?: string; digits?: boolean;
  } = {}) => (
    <div className={fieldClass(name)}>
      {label(key, options.required)}
      <input
        name={name}
        type={options.type ?? 'text'}
        value={form[name] as string}
        onChange={options.digits ? onDigits : onInput}
        inputMode={options.digits ? 'numeric' : undefined}
        maxLength={options.maxLength}
        aria-invalid={Boolean(errors[name])}
        aria-describedby={describedBy(name)}
      />
      {fieldError(name)}
    </div>
  );

  const pick = (file: File | null) => {
    setMandateFile(file);
    setErrors((current) => {
      if (!current.mandateForm) return current;
      const next = { ...current };
      delete next.mandateForm;
      return next;
    });
  };

  // ------------------------------------------------------------------ what the order will be

  const billingDay = Number(form.billingDay);
  const firstDate = form.billingDay ? firstCollectionDate(billingDay, form.mandate, lockedDates) : null;
  const payments = parsePayments(form);
  const amount = parseAmount(form.amount);
  const lastDate = firstDate && payments !== undefined ? lastCollectionDate(firstDate, payments, billingDay) : null;

  const orderLine = (() => {
    if (amount == null || !firstDate || payments === undefined) return '';
    return payments == null
      ? t(`${PREFIX}.summaryUnlimited`, { amount: money(amount), day: billingDay, first: dateText(firstDate) })
      : t(`${PREFIX}.summaryLimited`, {
        amount: money(amount), day: billingDay, count: payments, first: dateText(firstDate), last: lastDate ? dateText(lastDate) : '',
      });
  })();

  const accountLine = [
    form.bank && t('standingOrders.accountPartBank', { value: form.bank }),
    form.branch && t('standingOrders.accountPartBranch', { value: form.branch }),
    form.account && t('standingOrders.accountPartAccount', { value: form.account }),
  ].filter(Boolean).join(' · ');

  const projectLine = currentFund ? `${currentFund.name} · ${t(`${PREFIX}.projectNumber`, { id: currentFund.id })}` : String(fundId);

  // ------------------------------------------------------------------ steps

  const choice = (value: Exclude<MandateAnswer, ''>, key: string) => (
    <label className={`${ownStyles.choice} ${form.mandate === value ? ownStyles.choiceOn : ''}`}>
      <input
        type="radio"
        name="mandate"
        value={value}
        checked={form.mandate === value}
        onChange={() => update({ mandate: value })}
        aria-describedby={describedBy('mandate')}
      />
      {t(`${PREFIX}.${key}`)}
    </label>
  );

  const deliveryChoice = (value: 'self' | 'office', key: string) => (
    <label className={`${ownStyles.choice} ${form.faxBy === value ? ownStyles.choiceOn : ''}`}>
      <input
        type="radio"
        name="faxBy"
        value={value}
        checked={form.faxBy === value}
        onChange={() => update({ faxBy: value })}
        aria-describedby={describedBy('faxBy')}
      />
      {t(`${PREFIX}.${key}`)}
    </label>
  );

  const delivery = formDeliveryOf(form);

  const mandateStep = (
    <div className={styles.stepContent}>
      <h3 className="body-md-medium text-(--font-body-3)">{t(`${PREFIX}.mandateQuestion`)}</h3>
      <div className={ownStyles.choices} role="radiogroup" aria-label={t(`${PREFIX}.mandateQuestion`)}>
        {choice('exists', 'mandateExists')}
        {choice('missing', 'mandateMissing')}
      </div>
      {fieldError('mandate')}
    </div>
  );

  const formStep = (
    <div className={styles.stepContent}>
      <h3 className="body-md-medium text-(--font-body-3)">{t(`${PREFIX}.mandateTitle`)}</h3>
      <p className={styles.fieldHint}>{t(`${PREFIX}.mandateInstruction`)}</p>
      <div className={`${ownStyles.filePicker} ${errors.mandateForm ? ownStyles.filePickerInvalid : ''}`}>
        <input
          ref={fileInputRef}
          type="file"
          accept={MANDATE_FORM_ACCEPT}
          className={ownStyles.hiddenInput}
          aria-label={t(`${PREFIX}.mandateChoose`)}
          aria-describedby={describedBy('mandateForm')}
          onChange={(e) => { pick(e.target.files?.[0] ?? null); e.target.value = ''; }}
        />
        {mandateFile ? (
          <>
            <span className={ownStyles.fileName}>{mandateFile.name}</span>
            <button type="button" className={ownStyles.fileButton} onClick={() => fileInputRef.current?.click()}>
              {t(`${PREFIX}.mandateReplace`)}
            </button>
            <button type="button" className={ownStyles.fileButton} onClick={() => pick(null)}>
              {t(`${PREFIX}.mandateRemove`)}
            </button>
          </>
        ) : (
          <button type="button" className={ownStyles.fileButton} onClick={() => fileInputRef.current?.click()}>
            {t(`${PREFIX}.mandateChoose`)}
          </button>
        )}
      </div>
      {fieldError('mandateForm')}

      <h3 className="body-md-medium text-(--font-body-3)">{t(`${PREFIX}.deliveryTitle`)}</h3>
      {form.bank === BANK_BY_EMAIL ? (
        <p className={styles.fieldHint}>{t(`${PREFIX}.deliveryByEmail`)}</p>
      ) : (
        <>
          <div className={ownStyles.choices} role="radiogroup" aria-label={t(`${PREFIX}.deliveryTitle`)}>
            {deliveryChoice('office', 'deliveryOffice')}
            {deliveryChoice('self', 'deliverySelf')}
          </div>
          {form.faxBy === 'self' && (
            <p className={styles.fieldHint}>
              {t(`${PREFIX}.deliverySelfHint`)}{' '}
              <a href={BRANCH_FAX_LOOKUP_URL} target="_blank" rel="noopener noreferrer">{t(`${PREFIX}.faxLookup`)}</a>
            </p>
          )}
          {fieldError('faxBy')}
        </>
      )}
    </div>
  );

  const detailsStep = (
    <div className={styles.stepContent}>
      <h3 className="body-md-medium text-(--font-body-3)">{t(`${PREFIX}.detailsTitle`)}</h3>
      <div className={styles.fieldsGrid} style={{ overflow: 'visible' }}>
        <div className={styles.field}>
          <label>{t(`${PREFIX}.project`)}</label>
          <input value={projectLine} readOnly disabled />
        </div>
        {textField('donorName', `${PREFIX}.donorName`, { required: true, maxLength: 50 })}
        {textField('israeliId', `${CREDIT}.israeliId`, { required: true, maxLength: 9, digits: true })}
        {textField('email', `${CREDIT}.email`, { type: 'email', maxLength: 50 })}
        {/* Stacked so the bank's list opens over the branch below it, not under it. */}
        <div className={ownStyles.selectField} style={{ zIndex: 3 }}>
          <SearchableSelect
            label={t(`${PREFIX}.bank`)}
            id="bank-order-bank"
            width="100%"
            value={form.bankDisplay}
            onChange={(value) => update({ bank: value.split(' - ')[0], bankDisplay: value, branch: '', branchDisplay: '' })}
            loadOptions={useBanks.getBankOptions}
            placeholder={`${PREFIX}.bankPlaceholder`}
          />
          {fieldError('bank')}
        </div>
        <div className={ownStyles.selectField} style={{ zIndex: 2 }}>
          <SearchableSelect
            label={t(`${PREFIX}.branch`)}
            id="bank-order-branch"
            width="100%"
            value={form.branchDisplay}
            onChange={(value) => update({ branch: value.split(' - ')[0], branchDisplay: value })}
            loadOptions={useBanks.getBranchOptions}
            dependency={form.bankDisplay}
            placeholder={`${PREFIX}.branchPlaceholder`}
            dependencyPlaceholder={`${PREFIX}.selectBankFirst`}
          />
          {fieldError('branch')}
        </div>
        {textField('account', `${PREFIX}.account`, { required: true, maxLength: 9, digits: true })}
        <div className={fieldClass('amount')}>
          {label(`${CREDIT}.monthlyAmount`, true)}
          <div className={styles.inputWithPrefix}>
            <span className={styles.inputPrefix} aria-hidden="true">₪</span>
            <input
              name="amount"
              type="number"
              min="1"
              step="0.01"
              value={form.amount}
              onChange={onInput}
              aria-invalid={Boolean(errors.amount)}
              aria-describedby={describedBy('amount')}
              onWheel={(e) => (e.target as HTMLInputElement).blur()}
            />
          </div>
          {fieldError('amount')}
        </div>
        <div className={fieldClass('payments')}>
          {label(`${CREDIT}.numberOfPayments`, true)}
          <input
            name="payments"
            type="number"
            min="1"
            value={form.unlimited ? '' : form.payments}
            onChange={onInput}
            disabled={form.unlimited}
            aria-invalid={Boolean(errors.payments)}
            aria-describedby={describedBy('payments')}
            onWheel={(e) => (e.target as HTMLInputElement).blur()}
          />
          <label className={ownStyles.checkboxRow}>
            <input
              type="checkbox"
              checked={form.unlimited}
              onChange={(e) => update({ unlimited: e.target.checked, payments: '' })}
            />
            {t(`${CREDIT}.unlimitedPayments`)}
          </label>
          {fieldError('payments')}
        </div>
        <div className={fieldClass('billingDay')}>
          {label(`${CREDIT}.billingDay`, true)}
          <select
            name="billingDay"
            value={form.billingDay}
            onChange={onInput}
            aria-invalid={Boolean(errors.billingDay)}
            aria-describedby={describedBy('billingDay')}
          >
            <option value="">{t(`${CREDIT}.chooseBillingDay`)}</option>
            {BANK_BILLING_DAYS.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
          {/* The first collection, worked out as soon as the day is chosen - the coordinator does not pick it. */}
          <span className={styles.fieldHint}>
            {firstDate
              ? t(form.mandate === 'missing' ? `${PREFIX}.firstCollectionAfterSetup` : `${PREFIX}.firstCollectionOn`, {
                date: dateText(firstDate), days: MANDATE_SETUP_DAYS,
              })
              : t(`${PREFIX}.billingDayHint`)}
          </span>
          {fieldError('billingDay')}
        </div>
      </div>
    </div>
  );

  const summaryStep = (
    <div className={styles.stepContent}>
      {/* Everything the order will be, restated: the steps behind this one are off screen. */}
      <dl className={ownStyles.summary}>
        <dt>{t(`${PREFIX}.project`)}</dt>
        <dd>{projectLine}</dd>
        <dt>{t(`${PREFIX}.summaryDonor`)}</dt>
        <dd>{form.donorName.trim()} · {form.israeliId}</dd>
        <dt>{t(`${PREFIX}.summaryAccount`)}</dt>
        <dd>{accountLine}</dd>
        <dt>{t(`${PREFIX}.summaryOrder`)}</dt>
        <dd><b>{orderLine}</b></dd>
        {form.mandate === 'missing' && mandateFile && (
          <>
            <dt>{t(`${PREFIX}.summaryForm`)}</dt>
            <dd>{mandateFile.name}{delivery && ` · ${t(`${PREFIX}.summaryDelivery_${delivery}`)}`}</dd>
          </>
        )}
      </dl>
      {/* What pressing the button does, said before it is pressed. */}
      <p className={styles.taxNotice}>
        {t(form.mandate === 'missing' && delivery ? `${PREFIX}.whatHappens_${delivery}` : `${PREFIX}.whatHappens`)}
      </p>
      {status === 'error' && errorMessage && (
        <div className={styles.errorMessage} role="alert">{errorMessage}</div>
      )}
    </div>
  );

  const passes = (found: BankOrderErrors) => {
    setErrors(found);
    return Object.keys(found).length === 0;
  };

  const steps: StepDefinition[] = [
    { content: mandateStep, validate: () => passes(validateMandateAnswer(form)) },
    { content: detailsStep, validate: () => passes(validateDetails(form)) },
    ...(form.mandate === 'missing'
      ? [{ content: formStep, validate: () => passes(validateMandateForm(mandateFile, form)) }]
      : []),
    { content: summaryStep },
  ];

  const handleSubmit = async () => {
    if (!firstDate || (form.mandate === 'missing' && !mandateFile)) return;
    setStatus('submitting');
    setErrorMessage('');
    try {
      const attached = form.mandate === 'missing' && mandateFile
        ? { fileName: mandateFile.name, contentType: mandateFile.type, base64: await readBase64(mandateFile) }
        : null;
      createMutation.mutate(buildCreateRequest(form, requestId, firstDate, attached));
    } catch {
      setStatus('error');
      setErrorMessage(t(`${PREFIX}.errors.mandateFormUnreadable`));
    }
  };

  const resetForm = () => {
    setForm(emptyBankOrderForm());
    setMandateFile(null);
    setErrors({});
    setCreated(null);
    setStatus('ready');
    setErrorMessage('');
    setRequestId(newRequestId());
  };

  const requestClose = () => {
    // Closing mid-submit would leave the write in flight with nothing watching for its answer.
    if (status === 'submitting') return;
    if (status !== 'success' && hasInput(form, mandateFile !== null)) {
      setShowDiscardWarning(true);
      return;
    }
    if (status === 'success') onRefreshList?.();
    onClose();
  };

  // Re-assigned every render so the dialog always asks the current form state, not a stale one.
  useEffect(() => {
    closeRequestRef.current = requestClose;
    return () => { closeRequestRef.current = null; };
  });

  const createdFirst = created ? readIsoDate(created.firstCollection) : null;
  /*
   * After the first collection, what is still the coordinator's to do: fax the form he said he would,
   * or - when the form could not be sent - hand it to the office. The order itself is set up either way.
   */
  const successNotes = [
    createdFirst && t(`${PREFIX}.successFirstCollection`, { date: dateText(createdFirst) }),
    created?.formSent === false && t(`${PREFIX}.successFormNotSent`),
    created?.formSent !== false && form.mandate === 'missing' && delivery === 'self' && t(`${PREFIX}.successFaxReminder`),
  ].filter(Boolean).join(' ');

  return (
    <>
      {status === 'success' ? (
        <SuccessScreen
          title={t(`${CREDIT}.successTitle`)}
          description={t(`${PREFIX}.successDescription`)}
          additionalText={successNotes || undefined}
          primaryButtonText={t(`${CREDIT}.backToList`)}
          primaryButtonAction={() => { onClose(); onRefreshList?.(); }}
          secondaryButtonText={t(`${CREDIT}.createAnother`)}
          secondaryButtonAction={resetForm}
        />
      ) : (
        <FormStepper
          steps={steps}
          onSubmit={handleSubmit}
          isSubmitting={status === 'submitting'}
          submitLabel={t(`${CREDIT}.submit`)}
        />
      )}
      <ConfirmationDialog
        open={showDiscardWarning}
        onOpenChange={(open) => { if (!open) setShowDiscardWarning(false); }}
        title={t(`${CREDIT}.discardTitle`)}
        message={t(`${CREDIT}.discardMessage`)}
        confirmText={`${CREDIT}.discardConfirm`}
        cancelText={`${CREDIT}.discardCancel`}
        variant="danger"
        onConfirm={() => { setShowDiscardWarning(false); onClose(); }}
        onCancel={() => setShowDiscardWarning(false)}
      />
    </>
  );
};

export default NewBankStandingOrder;
